import { useState } from 'react'
import { Search, Book, MessageCircle, Video, ExternalLink, ChevronRight, HelpCircle, Zap, Shield, CreditCard, FileText, Play, Clock } from 'lucide-react'
import { Link } from 'react-router-dom'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { FluentCard } from '@/components/layout/page-header'

const popularArticles = [
  {
    id: '1',
    title: 'Getting Started with Templatean',
    description: 'Learn the basics of creating and managing templates',
    category: 'Getting Started',
    readTime: '5 min',
    icon: Book,
    gradient: 'from-blue-400 to-blue-600',
  },
  {
    id: '2',
    title: 'Using AI to Generate Content',
    description: 'Master the AI Studio for automated content creation',
    category: 'AI Features',
    readTime: '8 min',
    icon: Zap,
    gradient: 'from-amber-400 to-orange-600',
  },
  {
    id: '3',
    title: 'Managing Team Permissions',
    description: 'Control access and permissions for your team members',
    category: 'Administration',
    readTime: '6 min',
    icon: Shield,
    gradient: 'from-emerald-400 to-green-600',
  },
  {
    id: '4',
    title: 'Billing and Subscription Guide',
    description: 'Understanding plans, billing cycles, and payment methods',
    category: 'Billing',
    readTime: '4 min',
    icon: CreditCard,
    gradient: 'from-violet-400 to-purple-600',
  },
]

const faqCategories = [
  {
    name: 'General',
    questions: [
      {
        q: 'What is Templatean?',
        a: 'Templatean is an AI-powered platform for creating, managing, and automating document templates. It helps businesses streamline their document workflows with intelligent automation.',
      },
      {
        q: 'How does the AI content generation work?',
        a: 'Our AI uses advanced language models to generate content based on your prompts and templates. You can customize the tone, style, and format to match your needs.',
      },
      {
        q: 'Is my data secure?',
        a: 'Yes, we use enterprise-grade encryption and security measures. Your data is encrypted at rest and in transit, and we comply with GDPR and other privacy regulations.',
      },
    ],
  },
  {
    name: 'Pricing',
    questions: [
      {
        q: 'What plans are available?',
        a: 'We offer three plans: Starter ($19/mo), Professional ($49/mo), and Enterprise ($199/mo). Each plan includes different features and usage limits.',
      },
      {
        q: 'Can I change my plan anytime?',
        a: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect at the start of your next billing cycle.',
      },
      {
        q: 'Do you offer refunds?',
        a: 'We offer a 14-day money-back guarantee for new subscriptions. After that, you can cancel anytime but no refunds are provided for partial months.',
      },
    ],
  },
  {
    name: 'Technical',
    questions: [
      {
        q: 'What file formats are supported?',
        a: 'We support DOCX, PDF, TXT, HTML, and various image formats (PNG, JPG, GIF). You can also export to multiple formats.',
      },
      {
        q: 'Is there an API available?',
        a: 'Yes, Professional and Enterprise plans include API access. Check our API documentation for integration details.',
      },
      {
        q: 'What browsers are supported?',
        a: 'Templatean works best on Chrome, Firefox, Safari, and Edge. We recommend using the latest version of your browser.',
      },
    ],
  },
]

const videoTutorials = [
  {
    id: '1',
    title: 'Platform Overview',
    duration: '3:45',
    thumbnail: '/tutorials/overview.jpg',
    category: 'Basics',
  },
  {
    id: '2',
    title: 'Creating Your First Template',
    duration: '5:20',
    thumbnail: '/tutorials/first-template.jpg',
    category: 'Templates',
  },
  {
    id: '3',
    title: 'AI Studio Deep Dive',
    duration: '8:15',
    thumbnail: '/tutorials/ai-studio.jpg',
    category: 'AI Features',
  },
  {
    id: '4',
    title: 'Workflow Automation',
    duration: '6:30',
    thumbnail: '/tutorials/workflows.jpg',
    category: 'Automation',
  },
]

const quickLinks = [
  {
    title: 'Documentation',
    description: 'Detailed guides and API docs',
    icon: Book,
    gradient: 'from-blue-400 to-blue-600',
    href: '/help/documentation',
  },
  {
    title: 'Video Tutorials',
    description: 'Step-by-step video guides',
    icon: Video,
    gradient: 'from-violet-400 to-purple-600',
    href: '/help/tutorials',
  },
  {
    title: 'FAQs',
    description: 'Quick answers to common questions',
    icon: HelpCircle,
    gradient: 'from-emerald-400 to-green-600',
    href: '/help/faq',
  },
  {
    title: 'Contact Support',
    description: 'Get help from our team',
    icon: MessageCircle,
    gradient: 'from-amber-400 to-orange-600',
    href: '/help/contact',
  },
]

export default function Help() {
  const [searchQuery, setSearchQuery] = useState('')
  const [_selectedCategory, _setSelectedCategory] = useState('all')

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Implement search functionality
    console.log('Searching for:', searchQuery)
  }

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-gray-900 to-gray-800 dark:from-gray-800 dark:to-gray-900 p-8 lg:p-12">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 text-center max-w-2xl mx-auto">
          <div className="inline-flex p-3 rounded-2xl bg-white/10 mb-6">
            <HelpCircle className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-3">
            How can we help?
          </h1>
          <p className="text-gray-300 mb-8">
            Search our knowledge base or browse topics below
          </p>

          <form onSubmit={handleSearch} className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search for articles, tutorials, or FAQs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-4 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all text-base"
            />
          </form>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {quickLinks.map((link) => {
          const Icon = link.icon
          return (
            <Link key={link.title} to={link.href}>
              <FluentCard className="h-full hover:scale-[1.02] transition-transform cursor-pointer">
                <div className="text-center">
                  <div className={`mx-auto h-14 w-14 rounded-2xl bg-gradient-to-br ${link.gradient} flex items-center justify-center mb-4 shadow-lg`}>
                    <Icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{link.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{link.description}</p>
                </div>
              </FluentCard>
            </Link>
          )
        })}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="articles" className="space-y-6">
        <TabsList className="inline-flex h-12 items-center justify-start gap-1 rounded-2xl bg-gray-100 dark:bg-gray-800 p-1.5">
          <TabsTrigger
            value="articles"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
          >
            <FileText className="h-4 w-4" />
            Popular Articles
          </TabsTrigger>
          <TabsTrigger
            value="videos"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
          >
            <Video className="h-4 w-4" />
            Video Tutorials
          </TabsTrigger>
          <TabsTrigger
            value="faq"
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-medium transition-all data-[state=active]:bg-white data-[state=active]:text-gray-900 data-[state=active]:shadow-sm dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-white"
          >
            <HelpCircle className="h-4 w-4" />
            FAQs
          </TabsTrigger>
        </TabsList>

        <TabsContent value="articles">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Popular Articles</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Most viewed documentation and guides</p>
              </div>
            </div>

            <div className="space-y-3">
              {popularArticles.map((article) => {
                const Icon = article.icon
                return (
                  <Link
                    key={article.id}
                    to={`/help/article/${article.id}`}
                    className="flex items-center gap-4 p-4 rounded-2xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-all group"
                  >
                    <div className={`flex-shrink-0 h-12 w-12 rounded-xl bg-gradient-to-br ${article.gradient} flex items-center justify-center shadow-lg`}>
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-gray-900 dark:text-white">{article.title}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                        {article.description}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                          {article.category}
                        </span>
                        <span className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          {article.readTime} read
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors" />
                  </Link>
                )
              })}
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/help/documentation"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                View All Articles
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="videos">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-400 to-purple-600">
                <Video className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Video Tutorials</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Learn with step-by-step video guides</p>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              {videoTutorials.map((video) => (
                <div
                  key={video.id}
                  className="group cursor-pointer"
                  onClick={() => console.log('Play video:', video.id)}
                >
                  <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-2xl overflow-hidden">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="h-14 w-14 rounded-full bg-white/90 dark:bg-gray-900/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                        <Play className="h-6 w-6 text-gray-900 dark:text-gray-100 ml-1" />
                      </div>
                    </div>
                    <span className="absolute bottom-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium bg-black/70 text-white">
                      {video.duration}
                    </span>
                  </div>
                  <h3 className="font-medium text-gray-900 dark:text-white mt-3">{video.title}</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {video.category}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/help/tutorials"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                View All Tutorials
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </FluentCard>
        </TabsContent>

        <TabsContent value="faq">
          <FluentCard>
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-400 to-green-600">
                <HelpCircle className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Frequently Asked Questions</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Quick answers to common questions</p>
              </div>
            </div>

            <Accordion type="single" collapsible className="w-full">
              {faqCategories.map((category, categoryIndex) => (
                <div key={categoryIndex} className="mb-6 last:mb-0">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider mb-3">
                    {category.name}
                  </h3>
                  {category.questions.map((item, index) => (
                    <AccordionItem
                      key={`${categoryIndex}-${index}`}
                      value={`${categoryIndex}-${index}`}
                      className="border-b border-gray-200 dark:border-gray-700 last:border-0"
                    >
                      <AccordionTrigger className="text-left font-medium text-gray-900 dark:text-white hover:text-gray-600 dark:hover:text-gray-300 py-4">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600 dark:text-gray-400 pb-4">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </div>
              ))}
            </Accordion>

            <div className="mt-6 text-center">
              <Link
                to="/help/faq"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              >
                View All FAQs
                <ExternalLink className="h-4 w-4" />
              </Link>
            </div>
          </FluentCard>
        </TabsContent>
      </Tabs>

      {/* Contact CTA */}
      <div className="relative overflow-hidden rounded-none bg-gradient-to-r from-violet-600 to-purple-600 p-8 lg:p-10">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

        <div className="relative z-10 text-center">
          <div className="inline-flex p-3 rounded-2xl bg-white/20 mb-4">
            <MessageCircle className="h-8 w-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Still need help?</h3>
          <p className="text-white/80 mb-6 max-w-md mx-auto">
            Our support team is here to assist you with any questions or issues
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/help/contact"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold bg-white text-purple-600 hover:bg-gray-100 transition-colors shadow-lg"
            >
              Contact Support
            </Link>
            <Link
              to="/help/community"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium text-white border border-white/30 hover:bg-white/10 transition-colors"
            >
              Join Community
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}
